import { useMemo } from 'react'

import { BidType, FCRD_MARKET_PROGRAMS, MFRR_MARKET_PROGRAMS } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { useFeatureToggle } from '@/features/featureToggle/contexts/FeatureToggleContext'

export type BidOfferPriceRequirements = {
  /**
   * Whether prices should be displayed by activation group.
   * Required for MFRR UP/DOWN market programs.
   */
  requiresPriceByActivationGroup: boolean

  /**
   * Whether to filter activation groups to only C_AND_I resource category.
   * Enabled when ALL the following conditions are met for MFRR:
   * - Cascading bid page feature toggle is enabled
   * - Selected country is Finland (fi)
   * - Market program is MFRR UP or MFRR DOWN
   * - Bid type is CAPACITY
   * OR for FCR-D:
   * - FCR-D cascading feature toggle is enabled
   * - Selected country is Finland (fi)
   * - Market program is FCR-D UP Dynamic or FCR-D DOWN Dynamic
   * - Bid type is CAPACITY
   */
  requiresOnlyCAndIActivationGroups: boolean
}

/**
 * Hook that determines how prices should be displayed based on groupParams.
 * This centralizes the logic for conditional rendering of price views.
 *
 * @param groupParams - The grouped bid parameters
 * @returns An object indicating which price view should be used
 *
 * @example
 * ```tsx
 * const requirements = useBidOfferPriceDataRequirements(groupParams)
 * if (requirements.requiresPriceByActivationGroup) {
 *   return <BidOfferPriceByActivationGroup />
 * } else {
 *   return <BidOfferPriceAggregated />
 * }
 * ```
 */
export function useBidOfferPriceDataRequirements(groupParams: GroupedBidParams): BidOfferPriceRequirements {
  const { isEnabled } = useFeatureToggle()
  const { selectedCountry } = useBiddingContext()

  return useMemo(() => {
    // Prices should be displayed by activation group for MFRR UP/DOWN
    const requiresPriceByActivationGroup = MFRR_MARKET_PROGRAMS.includes(groupParams.marketProgram)

    // Filter to only C_AND_I activation groups when cascading is enabled for MFRR or FCR-D
    const isMfrrCascading =
      isEnabled('FLEX_PORTAL_BID_CASCADING_MFRR_ENABLED') &&
      selectedCountry === 'fi' &&
      MFRR_MARKET_PROGRAMS.includes(groupParams.marketProgram) &&
      groupParams.bidType === BidType.CAPACITY

    const isFcrdCascading =
      isEnabled('FLEX_PORTAL_BID_CASCADING_FCRD_ENABLED') &&
      selectedCountry === 'fi' &&
      FCRD_MARKET_PROGRAMS.includes(groupParams.marketProgram) &&
      groupParams.bidType === BidType.CAPACITY

    const requiresOnlyCAndIActivationGroups = isMfrrCascading || isFcrdCascading

    return {
      requiresPriceByActivationGroup,
      requiresOnlyCAndIActivationGroups,
    }
  }, [groupParams, isEnabled, selectedCountry])
}
